import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getPendingData } from "../helper/apicalls/user";
import PendingDataPopup from "../components/PendingDataPopup";
import Reports from "./Reports";
import GlobalLoader from "../components/GlobalLoader";
import MetricGrid from "../components/dashboard/MetricGrid";
import OverallScore from "../components/dashboard/OverallScore";
import SideStatCards from "../components/dashboard/SideStatCards";
import ContinueLearningCard from "../components/dashboard/ContinueLearningCard";
import CourseSubjectAnalysisCard from "../components/dashboard/CourseSubjectAnalysisCard";
import ActivityConsistencyCard from "../components/reports/ActivityConsistencyCard";
import {
  getDashboardOverview,
  getDashboardAnalytics,
  getSubjectDashboardOverview,
  getSubjectDashboardAnalytics,
  getTopicDashboardOverview,
  getTopicDashboardAnalytics,
  getSubtopicDashboardOverview,
  getSubtopicDashboardAnalytics,
} from "../helper/apicalls/dashboard";
import { getAllSubjects } from "../helper/apicalls/subject";
import { getAllTopics } from "../helper/apicalls/topic";
import { getAllSubtopics } from "../helper/apicalls/subtopic";
import "../styles/Dashboard.scss";
import "../styles/OverallScore.scss";
import { storage } from "../helper/cookieUtils";
import ErrorBoundary from "../components/ErrorBoundary";

const Dashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState();
  const [loading, setLoading] = useState(true);
  const [pendingData, setPendingData] = useState(null);
  const [showPendingPopup, setShowPendingPopup] = useState(false);

  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subtopics, setSubtopics] = useState([]);

  const [showFilters, setShowFilters] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : true,
  );

  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedTopic, setSelectedTopic] = useState("");
  const [selectedSubtopic, setSelectedSubtopic] = useState("");

  const fetchDashboardData = async (type = "all", key = "") => {
    setLoading(true);
    try {
      let overviewPromise, analyticsPromise;
      let params = {};

      if (type === "subject") {
        params = { key: key };
        overviewPromise = getSubjectDashboardOverview(params);
        analyticsPromise = getSubjectDashboardAnalytics(params);
      } else if (type === "topic") {
        params = { key: key };
        overviewPromise = getTopicDashboardOverview(params);
        analyticsPromise = getTopicDashboardAnalytics(params);
      } else if (type === "subtopic") {
        params = { key: key };
        overviewPromise = getSubtopicDashboardOverview(params);
        analyticsPromise = getSubtopicDashboardAnalytics(params);
      } else {
        overviewPromise = getDashboardOverview();
        analyticsPromise = getDashboardAnalytics();
      }

      const [overviewData, analyticsData] = await Promise.all([
        overviewPromise,
        analyticsPromise,
      ]);

      const overview = overviewData?.data || overviewData || {};
      const analytics = analyticsData?.data || analyticsData || {};

      const apiData = { ...overview, ...analytics };

      if (apiData) {
        let analysisData = {};
        if (type === "subtopic") {
          analysisData = {};
        } else if (type === "topic") {
          analysisData = apiData.topic_subtopic_analysis || {};
        } else {
          analysisData =
            apiData.subject_topic_analysis ||
            apiData.course_subject_analysis ||
            {};
        }

        const courseSubjectAnalysisArray = Object.values(analysisData);

        const progressMastery = apiData.progress_based_mastery || {};
        const masteryCategories = [
          "application",
          "critical_thinking",
          "direct",
          "reasoning",
        ];

        const withTotal = (item) => {
          if (!item) return item;
          const sum = masteryCategories.reduce(
            (acc, key) => acc + (item[key]?.percentage || 0),
            0,
          );
          return {
            ...item,
            total_percentage: Math.round(sum / (masteryCategories.length || 1)),
          };
        };

        const subjectCategoryWithScores = courseSubjectAnalysisArray.map(
          (subject) => {
            const mastery = progressMastery[subject.key] || {};
            const masteryWithTotal = withTotal(mastery);

            return {
              subject_key: subject.key,
              subject_name: subject.name,
              total_percentage: subject.percentage || 0,
              ...masteryWithTotal,
            };
          },
        );

        const overall_categories = withTotal(progressMastery.overall || {});

        const masteryScore = Object.entries(progressMastery)
          .filter(([key]) => key !== "overall")
          .map(([key, value]) => {
            const itemWithTotal = withTotal(value);
            return {
              subject_key: key,
              subject_name: value.name,
              ...itemWithTotal,
            };
          });

        const totalTimeSpent = apiData.total_time_spent || {};
        const time_spent_per_subject = Object.values(totalTimeSpent)
          .filter((item) => item && item.key)
          .map((item) => ({
            key: item.key,
            name: item.name,
            time_spent: item.time_spent || [],
          }));

        const overall_time_spent = totalTimeSpent.overall?.time_spent || [];

        const arOverall =
          apiData.active_recall_summary?.overall ||
          apiData.active_recall?.overall ||
          {};
        const total_recall =
          arOverall.total_recall ?? apiData.total_recall ?? 0;
        const total_recall_completed =
          arOverall.total_recall_completed ??
          apiData.total_recall_completed ??
          0;

        const alOverall =
          apiData.adaptive_learning_summary?.overall ||
          apiData.adaptive_learning?.overall ||
          {};
        const total_adaptive =
          alOverall.total_adaptive ??
          apiData.total_adaptive ??
          apiData.adaptives ??
          0;
        const total_adaptive_completed =
          alOverall.total_adaptive_completed ??
          apiData.total_adaptive_completed ??
          0;

        setDashboardData({
          ...apiData,
          subject_category: subjectCategoryWithScores,
          overall_categories,
          masteryScore,
          course_subject_analysis_array: courseSubjectAnalysisArray,
          time_spent_per_subject,
          overall_time_spent,
          showAnalysisCard: type !== "subtopic",
          total_recall,
          total_recall_completed,
          total_adaptive,
          total_adaptive_completed,
        });
      }
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const selectedSubjectName = useMemo(() => {
    if (!selectedSubject) return "Overall";
    const subject = subjects.find((s) => s.key === selectedSubject);
    return subject?.name || "Subject";
  }, [selectedSubject, subjects]);

  const selectedTopicName = useMemo(() => {
    if (!selectedTopic) return "All Topics";
    const topic = topics.find((t) => t.key === selectedTopic);
    return topic?.name || "Topic";
  }, [selectedTopic, topics]);

  const selectedSubtopicName = useMemo(() => {
    if (!selectedSubtopic) return "All Subtopics";
    const subtopic = subtopics.find((s) => s.key === selectedSubtopic);
    return subtopic?.name || "Subtopic";
  }, [selectedSubtopic, subtopics]);

  const mobileFilterLabel = useMemo(() => {
    if (selectedSubtopic) return "Subtopic";
    if (selectedTopic) return "Topic";
    return "Subject";
  }, [selectedSubtopic, selectedTopic]);

  const mobileFilterName = useMemo(() => {
    if (selectedSubtopic) return selectedSubtopicName;
    if (selectedTopic) return selectedTopicName;
    return selectedSubjectName;
  }, [
    selectedSubtopic,
    selectedTopic,
    selectedSubtopicName,
    selectedTopicName,
    selectedSubjectName,
  ]);

  const fetchInitialFilters = async () => {
    try {
      const courseKey = storage.getItem("courseKey");
      if (courseKey) {
        const subjectData = await getAllSubjects(courseKey);
        setSubjects(
          Array.isArray(subjectData) ? subjectData : subjectData?.data || [],
        );
      }
    } catch (error) {
      console.error("Failed to fetch initial filters:", error);
    }
  };

  const handleSubjectChange = async (e) => {
    const key = e.target.value;
    setSelectedSubject(key);
    setSelectedTopic("");
    setSelectedSubtopic("");
    setTopics([]);
    setSubtopics([]);

    if (key) {
      fetchDashboardData("subject", key);
      try {
        const topicData = await getAllTopics(key);
        setTopics(Array.isArray(topicData) ? topicData : topicData?.data || []);
      } catch (error) {
        console.error("Failed to fetch topics:", error);
      }
    } else {
      fetchDashboardData();
    }
  };

  const handleTopicChange = async (e) => {
    const key = e.target.value;
    setSelectedTopic(key);
    setSelectedSubtopic("");
    setSubtopics([]);

    if (key) {
      fetchDashboardData("topic", key);
      try {
        const subtopicData = await getAllSubtopics(key);
        setSubtopics(
          Array.isArray(subtopicData) ? subtopicData : subtopicData?.data || [],
        );
      } catch (error) {
        console.error("Failed to fetch subtopics:", error);
      }
    } else {
      fetchDashboardData("subject", selectedSubject);
    }
  };

  const handleSubtopicChange = async (e) => {
    const key = e.target.value;
    setSelectedSubtopic(key);

    if (key) {
      fetchDashboardData("subtopic", key);
    } else {
      fetchDashboardData("topic", selectedTopic);
    }
  };

  const metrics = useMemo(
    () => [
      {
        type: "quiz",
        totalKey: "total_quiz",
        total: dashboardData?.total_quiz ?? 0,
        completed: dashboardData?.total_quiz_completed ?? 0,
      },
      {
        type: "test",
        totalKey: "total_test",
        total: dashboardData?.total_test ?? 0,
        completed: dashboardData?.total_test_completed ?? 0,
      },
      {
        type: "page",
        totalKey: "total_page",
        total: dashboardData?.total_page ?? 0,
        completed: dashboardData?.total_completed_pages ?? 0,
      },
    ],
    [dashboardData],
  );

  const sideStats = useMemo(
    () => [
      {
        title: "Active Recall",
        total: dashboardData?.active_recalls ?? 0,
        completed:
          dashboardData?.active_recall_mastery.overall.total_recall_completed ??
          0,
      },
      {
        title: "Adaptive Learning",
        total:
          dashboardData?.adaptive_learning_mastery.overall
            .total_adaptive_learning ?? 0,
        completed:
          dashboardData?.adaptive_learning_mastery.overall
            .total_adaptive_learning_completed ?? 0,
      },
    ],
    [dashboardData],
  );

  const fetchPendingData = async () => {
    const hasShown = storage.getItem("hasShownPendingPopup");
    if (hasShown) return;

    try {
      const response = await getPendingData();
      const hasRewards =
        response?.data?.login_coins > 0 ||
        response?.data?.login_xp > 0 ||
        response?.data?.badge_name;

      if (response && (response.has_pending || hasRewards)) {
        setPendingData(response.data);
        setShowPendingPopup(true);
        storage.setItem("hasShownPendingPopup", "true");

        const storedUser = storage.getItem("user");
        if (storedUser && response.data) {
          try {
            const user = JSON.parse(storedUser);
            const updatedUser = {
              ...user,
              total_coins: response.data.total_coins ?? user.total_coins,
              total_xp: response.data.total_xp ?? user.total_xp,
              level: response.data.level ?? user.level,
              badge_name: response.data.badge_name ?? user.badge_name,
            };
            storage.setItem("user", JSON.stringify(updatedUser));
            window.dispatchEvent(new Event("user_data_updated"));
          } catch (e) {
            console.error("Error updating user cookie:", e);
          }
        }
      }
    } catch (error) {
      console.error("Failed to fetch pending data:", error);
    }
  };

  const handleStartLearning = useCallback(() => {
    const courseKey = storage.getItem("courseKey");
    if (courseKey) {
      navigate(`/courses/${courseKey}`);
    } else {
      console.warn("No courseKey found in Cookies");
    }
  }, [navigate]);

  useEffect(() => {
    fetchDashboardData();
    fetchPendingData();
    fetchInitialFilters();

    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);

      if (!mobile) {
        setShowFilters(true);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMobile &&
        showFilters &&
        !event.target.closest(".dashboard-filter-pill-wrapper") &&
        !event.target.closest(".dashboard-filters-container")
      ) {
        setShowFilters(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobile, showFilters]);

  return (
    <>
      {loading && <GlobalLoader />}

      <div className="dashboard-filter-wrapper">
        <div className="dashboard-filter-pill-wrapper">
          <button
            type="button"
            className={`dashboard-filter-pill ${showFilters ? "is-active" : ""}`}
            onClick={() => setShowFilters((prev) => !prev)}
          >
            <span className="dashboard-filter-pill__label">
              {mobileFilterLabel}
            </span>
            <span className="dashboard-filter-pill__value">
              {/* <span className="dashboard-filter-pill__icon" /> */}
              <span className="dashboard-filter-pill__text">
                {mobileFilterName}
              </span>
            </span>
          </button>
        </div>

        <div
          className={`dashboard-filters-container ${
            isMobile && !showFilters ? "is-collapsed" : "is-open"
          }`}
        >
          <div className="dashboard-filters-header">
            <button
              type="button"
              className="dashboard-filters-toggle"
              onClick={() => setShowFilters((prev) => !prev)}
            >
              <span className="dashboard-filters-toggle__icon" />
              <span className="dashboard-filters-toggle__label">Filters</span>
            </button>
          </div>

          {(!isMobile || showFilters) && (
            <div className="dashboard-filters-body">
              <div className="filter-group">
                <label>Subject</label>
                <select
                  value={selectedSubject}
                  onChange={handleSubjectChange}
                  className="filter-select"
                >
                  <option value="">Overall</option>
                  {subjects.map((subject) => (
                    <option key={subject.key} value={subject.key}>
                      {subject.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Topic</label>
                <select
                  value={selectedTopic}
                  onChange={handleTopicChange}
                  className="filter-select"
                  disabled={!selectedSubject}
                >
                  <option value="">All Topics</option>
                  {topics.map((topic) => (
                    <option key={topic.key} value={topic.key}>
                      {topic.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Subtopic</label>
                <select
                  value={selectedSubtopic}
                  onChange={handleSubtopicChange}
                  className="filter-select"
                  disabled={!selectedTopic}
                >
                  <option value="">All Subtopics</option>
                  {subtopics.map((subtopic) => (
                    <option key={subtopic.key} value={subtopic.key}>
                      {subtopic.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="dashboard-container custom-scrollbar">
        <MetricGrid metrics={metrics} />

        <div className="dashboard-section dashboard-section--primary">
          <div className="dashboard-section__main-card">
            <div className="card-styled-wrapper overall-score-card">
              <div className="card-content card-content--flush">
                <OverallScore
                  masteryScore={dashboardData?.masteryScore || []}
                  overallMastery={dashboardData?.overall_categories || {}}
                  dashboard={false}
                />
              </div>
            </div>
          </div>

          <div className="dashboard-section__side-card">
            <SideStatCards stats={sideStats} />
          </div>
        </div>

        <div className="dashboard-section dashboard-section--dual">
          <div className="dashboard-section__column">
            <ContinueLearningCard
              onStartLearning={handleStartLearning}
              data={dashboardData?.continue_learning}
            />
          </div>

          <div className="dashboard-section__column dashboard-section__column--calendar">
            <ActivityConsistencyCard
              currentStreak={dashboardData?.current_streak || []}
              showTitle={true}
            />
          </div>
        </div>

        <Reports data={dashboardData} />
      </div>
      <ErrorBoundary>
        <PendingDataPopup
          isOpen={showPendingPopup}
          onClose={() => setShowPendingPopup(false)}
          data={pendingData}
        />
      </ErrorBoundary>
    </>
  );
};

export default Dashboard;
